import { MainApi } from 'api/MainApi'
import { LineString } from 'geojson'
import cloneDeep from 'lodash.clonedeep'
import SelectFileModal from 'pages/Mission/SelectFileModal/SelectFileModal'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import { selectMissionEditingTransfer, selectMissionInfo, selectTransferRoutes } from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { FILES_LOADING_TYPE, TRANSFER_CALCULATING_STATUSES, TRANSFER_TYPE } from 'utils/constants'

import CommonRouteItem from './CommonRouteItem/CommonRouteItem'

const TransferRouteItem = ({
  type,
  zoneIdToTransfer,
  hideActions,
}: {
  type: TRANSFER_TYPE
  zoneIdToTransfer?: number
  hideActions?: boolean
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const transferRoutes = useSelector(selectTransferRoutes)
  const missionEditingTransfer = useSelector(selectMissionEditingTransfer)
  const missionInfo = useSelector(selectMissionInfo)
  const [showSelectRecordedRouteModal, setShowSelectRecordedRouteModal] = useState(false)
  const transferRoute = useMemo(() => {
    const route = transferRoutes.find(route => route.properties.zone_id_to === zoneIdToTransfer)
    return route
  }, [transferRoutes, zoneIdToTransfer])

  const onTransferRouteClicked = () => {
    dispatch(MissionActions.setHighlightedFeature(transferRoute))
    transferRoute?.id && dispatch(MissionActions.setHighlightedFeatureInList(transferRoute.id))
  }

  const onEditRouteClicked = () => {
    if (transferRoute) {
      store.dispatch(missionThunks.setEditingTransfer(transferRoute))
    }
  }

  const onRemoveRouteClicked = () => {
    if (transferRoute?.properties.id) {
      store.dispatch(missionThunks.deleteTransfer(transferRoute?.properties.id))
    }
  }

  const onCancelEditingClicked = (e: any) => {
    e.stopPropagation()
    store.dispatch(missionThunks.cancelEditingTransfer())
  }

  const onSaveClicked = (e: any) => {
    e.stopPropagation()
    store.dispatch(missionThunks.editTransfer())
  }

  const onCalculateClicked = async () => {
    try {
      if (transferRoute?.properties.id) {
        await MainApi.startCalculatingTransferRoute(transferRoute?.properties.id)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const onUseRecordedRoute = () => {
    setShowSelectRecordedRouteModal(true)
  }

  const onSelectRecordedRoute = async (fileId: number) => {
    try {
      if (transferRoute?.properties.id) {
        await MainApi.patchTransferRouteWithRecorded(transferRoute.properties.id, fileId)
        if (missionInfo?.id) {
          const mission = await MainApi.fetchMission(missionInfo.id)
          dispatch(MissionActions.setMission(mission))
        }
      }
      setShowSelectRecordedRouteModal(false)
    } catch (e) {
      console.error(e)
    }
  }

  const text = useMemo(() => {
    switch (type) {
      case TRANSFER_TYPE.START:
        return t('view_zones.transfer_route_auto_start')
      case TRANSFER_TYPE.MIDDLE:
        return t('view_zones.transfer_route_auto')
      case TRANSFER_TYPE.FINISH:
        return t('view_zones.transfer_route_auto_finish')
      default:
        return t('view_zones.transfer_route_auto')
    }
  }, [t, type])

  const editing = useMemo(() => {
    return missionEditingTransfer === transferRoute
  }, [missionEditingTransfer, transferRoute])

  return (
    <>
      {transferRoute?.id ? (
        <CommonRouteItem
          id={transferRoute.id}
          text={text}
          itemType={type}
          editing={editing}
          status={transferRoute.properties.status}
          length={transferRoute.properties.length}
          executionTime={transferRoute.properties.executionTime}
          onClick={onTransferRouteClicked}
          onEdit={onEditRouteClicked}
          onCancel={onCancelEditingClicked}
          onRemove={onRemoveRouteClicked}
          onSave={onSaveClicked}
          hideActions={hideActions}
          onCalculate={onCalculateClicked}
          onUseRecordedRoute={onUseRecordedRoute}
        />
      ) : null}
      {showSelectRecordedRouteModal && (
        <SelectFileModal
          isOpen={true}
          mode={FILES_LOADING_TYPE.ROUTE}
          onClose={() => setShowSelectRecordedRouteModal(false)}
          onConfirmClick={(name, id) => id && onSelectRecordedRoute(id)}
        />
      )}
    </>
  )
}

export default TransferRouteItem
